import type { TarsAnalyticsStore } from "../../../../stores/tars/tars-analytics-store";
import { FACET_CREATE_DEFAULT_VERSION } from "../../tars-constants";
import type { CreateFacetPayload } from "../../tars-hooks/useCreateFacet";
import type { SourceFile } from "./sources-types";
import { buildDataRepoFacetTemplate } from "./dataRepoFacetTemplate";

type CreateFacetMutate = (
  payload: CreateFacetPayload,
  options?: {
    onSuccess?: (resp: unknown) => void;
  }
) => void;

export type ApplyFacetTemplateResult =
  | "applied"
  | "needs_replace_confirm"
  | "invalid";

export function applyDataRepoFacetTemplateFromSource(
  analytics: TarsAnalyticsStore,
  opts: {
    notebookId: number;
    selectedCanvasId: number;
    fileNode: SourceFile;
    createFacetMutate: CreateFacetMutate;
    /** When true, overwrite existing facet JSON without asking (caller already confirmed). */
    confirmReplace?: boolean;
  }
): ApplyFacetTemplateResult {
  const {
    notebookId,
    selectedCanvasId,
    fileNode,
    createFacetMutate,
    confirmReplace = false,
  } = opts;
  const sourceId = Number(fileNode.id);
  const esIndex = fileNode.es_index?.trim() ?? "";
  if (
    !notebookId ||
    Number.isNaN(sourceId) ||
    sourceId <= 0 ||
    selectedCanvasId <= 0 ||
    !esIndex
  ) {
    return "invalid";
  }
  const template = buildDataRepoFacetTemplate(sourceId, esIndex);
  const jsonContent = JSON.stringify(template, null, 2);

  const selectedId = analytics.selectedFacetId;
  const existingFacet =
    selectedId != null
      ? analytics.facets.find((f) => f.id === selectedId)
      : undefined;

  const hasExistingJson = Boolean(existingFacet?.jsonContent?.trim());
  if (existingFacet && hasExistingJson && !confirmReplace) {
    return "needs_replace_confirm";
  }

  analytics.setSelectedSourceIds([fileNode.id]);
  analytics.setAnalyticsToolbarMode("json");

  if (existingFacet) {
    analytics.updateFacet(existingFacet.id, {
      etl_json: template,
      jsonContent,
      facet_result: {},
    });
    return "applied";
  }

  analytics.addFacet({
    name: fileNode.name || "Party Support",
    etl_json: template,
    jsonContent,
    facet_result: {},
    more: {},
  });
  const newFacetLocalId = analytics.selectedFacetId;
  createFacetMutate(
    {
      notebook_id: notebookId,
      source_id: sourceId,
      canvas_id: selectedCanvasId,
      etl_json: template,
      facet_result: {},
      version: FACET_CREATE_DEFAULT_VERSION,
      more: {},
    },
    {
      onSuccess: (resp) => {
        if (!newFacetLocalId || resp == null || typeof resp !== "object") return;
        const data = resp as Record<string, unknown>;
        const facetIdRaw = data.facet_id ?? data.id;
        const facetId =
          typeof facetIdRaw === "number" ? facetIdRaw : Number(facetIdRaw);
        if (!Number.isFinite(facetId) || facetId <= 0) return;
        analytics.updateFacet(newFacetLocalId, { facet_id: facetId });
      },
    }
  );
  return "applied";
}
